/**
 * MemorizationAudioStorage — local-first storage for the user's own
 * recitation recordings, indexed by validation id.
 *
 * Stays entirely client-side (IndexedDB). Nothing is uploaded to the server.
 * Cascade deletion on plan removal is handled by `deleteByPlan(planId)`.
 *
 * Object store: `memorization_recordings`
 *  Key: validation_id (string, ObjectId)
 *  Value: { id, plan_id, score, label, surah_number, from_ayah, to_ayah,
 *           transcribed, created_at, blob, mime, size_bytes }
 */

const DB_NAME = 'noor_memorization_audio_db';
const DB_VERSION = 1;
const STORE = 'memorization_recordings';

const isBrowser = typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

function openDB() {
  return new Promise((resolve, reject) => {
    if (!isBrowser) return reject(new Error('IndexedDB unavailable'));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: 'id' });
        s.createIndex('by_plan', 'plan_id');
        s.createIndex('by_created', 'created_at');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function promisify(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode = 'readonly') {
  const db = await openDB();
  return db.transaction(STORE, mode).objectStore(STORE);
}

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

export const memorizationAudioStorage = {
  /** Save a recording. Returns the persisted record (without blob). */
  async save({ validationId, planId, score, label, surahNumber, fromAyah, toAyah,
               transcribed, blob }) {
    if (!isBrowser) throw new Error('IndexedDB unavailable');
    if (!validationId) throw new Error('validationId required');
    if (!blob) throw new Error('blob required');
    if (blob.size > MAX_BYTES) {
      throw new Error(`Audio dépasse ${(MAX_BYTES / 1024 / 1024)} MB`);
    }
    const record = {
      id: validationId,
      plan_id: planId,
      score: score ?? null,
      label: label ?? null,
      surah_number: surahNumber,
      from_ayah: fromAyah,
      to_ayah: toAyah,
      transcribed: transcribed || '',
      created_at: new Date().toISOString(),
      blob,
      mime: blob.type || 'audio/webm',
      size_bytes: blob.size,
    };
    const store = await tx('readwrite');
    await promisify(store.put(record));
    const { blob: _drop, ...meta } = record;
    return meta;
  },

  /** Fetch a recording with its blob. */
  async get(validationId) {
    if (!isBrowser) return null;
    const store = await tx('readonly');
    return promisify(store.get(validationId));
  },

  /** Build a temporary object URL for the blob. Caller must revoke. */
  async getObjectUrl(validationId) {
    const rec = await this.get(validationId);
    if (!rec || !rec.blob) return null;
    return URL.createObjectURL(rec.blob);
  },

  /** List metadata for all recordings (no blobs). */
  async list({ planId = null } = {}) {
    if (!isBrowser) return [];
    const store = await tx('readonly');
    const all = await promisify(store.getAll());
    const out = (all || [])
      .filter((r) => (planId ? r.plan_id === planId : true))
      .map((r) => {
        const { blob: _drop, ...meta } = r;
        return meta;
      })
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return out;
  },

  async deleteOne(validationId) {
    if (!isBrowser) return;
    const store = await tx('readwrite');
    await promisify(store.delete(validationId));
  },

  async deleteByPlan(planId) {
    if (!isBrowser) return 0;
    const store = await tx('readwrite');
    const idx = store.index('by_plan');
    const req = idx.openCursor(IDBKeyRange.only(planId));
    let n = 0;
    return new Promise((resolve, reject) => {
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          cursor.delete();
          n += 1;
          cursor.continue();
        } else {
          resolve(n);
        }
      };
      req.onerror = () => reject(req.error);
    });
  },

  async storageUsage() {
    const list = await this.list();
    return {
      count: list.length,
      bytes: list.reduce((sum, r) => sum + (r.size_bytes || 0), 0),
    };
  },
};
